import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogTitle,
  Typography,
  DialogActions,
} from "@mui/material";
import Button from "@mui/material/Button";
import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import { appThemes, useMeetingAppContext } from "../MeetingAppContextDef";

const DarkCustomButton = styled(Button)`
  border-radius: 0.8vh;
  &:hover {
    color: white;
    background: #2b2e32;
  }
`;

const DarkOutlinedButton = styled(Button)`
  border-radius: 0.8vh;
  &:hover {
    color: white;
    background: #2b2e32;
    border: 0.2vh solid white !important;
  }
`;

const DarkDangerButton = styled(Button)`
  border-radius: 0.8vh;
  background: #FF2828CC;
  color: white;
  &:hover {
    background: #ff2828;
    color: white;
  }
  &:active {
    filter: brightness(0.95);
  }
`;

const LightCustomButton = styled(Button)`
  border-radius: 0.8vh;
  &:hover {
    background: #eef0f2;
  }
`;

const LightOutlinedButton = styled(Button)`
  border-radius: 0.8vh;
  &:hover {
    background: #eef0f2;
    border: 0.2vh solid #404b53 !important;
  }
`;

const LightDangerButton = styled(Button)`
  border-radius: 0.8vh;
  background: #FF2828CC;
  color: white;
  &:hover {
    background: #ff2828;
    color: white;
  }
  &:active {
    filter: brightness(0.95);
  }
`;
const ConfirmBox = ({
  successText,
  rejectText,
  onSuccess,
  open,
  onReject,
  title,
  subTitle,
  subTitleColor,
  appTheme,
  isDestructive = false, // New prop for destructive actions
}) => {
  const v = useMeetingAppContext();
  const theme = useTheme();

  if (v && v?.isRecorder) {
    return <></>;
  }

  const activeTheme = v?.appTheme ?? appTheme;
  const isLightTheme = activeTheme === appThemes.LIGHT;

  const ButtonElement = isLightTheme ? LightCustomButton : DarkCustomButton;

  let OutlinedButtonElememt;
  if (isDestructive) {
    OutlinedButtonElememt = isLightTheme ? LightDangerButton : DarkDangerButton;
  } else {
    OutlinedButtonElememt = isLightTheme ? LightOutlinedButton : DarkOutlinedButton;
  }

  const boxBgColor = activeTheme === appThemes.DARK
    ? theme.palette.darkTheme.slightLighter
    : isLightTheme
    ? theme.palette.lightTheme.main
    : theme.palette.background.default;

  const subTitleTextColor = subTitleColor
    ? subTitleColor
    : isLightTheme
    ? theme.palette.lightTheme.five
    : "#9FA0A7";

  const actionBtnBgColor = isDestructive
    ? theme.palette.error.main
    : isLightTheme ? "#95959E" : "white";

  const actionBtnHoverBgColor = isDestructive
    ? "#B71C1C"
    : isLightTheme ? "#7a7a7a" : "#e0e0e0";

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={() => {}}
      aria-labelledby="responsive-dialog-title"
    >
      <Box
        style={{
          padding: "1.5vh",
          backgroundColor: boxBgColor,
        }}
      >
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <DialogTitle
              style={{
                padding: "1.2vh 1.6vh",
                margin: 0,
                width: "100%",
                color: isLightTheme ? "#95959E" : "#fff",
              }}
              id="responsive-dialog-title"
            >
              <Typography
                variant="subtitle1"
                style={{
                  fontWeight: "bold",
                  fontSize: "2vh",
                  color: isLightTheme ? "#404B53" : "#fff",
                }}
              >
                {title}
              </Typography>
              {subTitle && (
                <Typography
                  variant="body2"
                  style={{
                    marginTop: "0.8vh",
                    fontSize: "1.6vh",
                    color: subTitleTextColor,
                  }}
                >
                  {subTitle}
                </Typography>
              )}
            </DialogTitle>
          </Box>
        </Box>
        <Box>
          <DialogActions
            style={{
              padding: "0.8vh 1.6vh 1.2vh",
              gap: "1vh",
            }}
          >
            <ButtonElement
              onClick={onReject}
              sx={{
                color: "white",
                padding: "1vh 2.4vh",
                fontSize: "1.6vh",
                textTransform: "none",
                minWidth: "10vh",
                border: "0.1vh solid rgba(255, 255, 255, 0.5)",
                background: "transparent",
                "&:hover": {
                  background: "transparent",
                  borderColor: "rgba(255, 255, 255, 0.8)",
                },
              }}
            >
              {rejectText}
            </ButtonElement>

            <OutlinedButtonElememt
              onClick={onSuccess}
              sx={{
                color: "white",
                padding: "1vh 2.4vh",
                fontSize: "1.6vh",
                textTransform: "none",
                minWidth: "10vh",
                border: "none",
                backgroundColor: actionBtnBgColor,
                "&:hover": {
                  backgroundColor: actionBtnHoverBgColor,
                },
              }}
              autoFocus
            >
              {successText}
            </OutlinedButtonElememt>
          </DialogActions>
        </Box>
      </Box>
    </Dialog>
  );
};

ConfirmBox.propTypes = {
  successText: PropTypes.string,
  rejectText: PropTypes.string,
  onSuccess: PropTypes.func,
  open: PropTypes.bool,
  onReject: PropTypes.func,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  subTitleColor: PropTypes.string,
  appTheme: PropTypes.string,
  isDestructive: PropTypes.bool,
};

export default ConfirmBox;
